import React, { useContext, useEffect } from 'react'
import styled from 'styled-components'
import { useInView } from 'react-intersection-observer'
import Accordion from '../shared/Accordion'
import { LayoutContext, useUI } from '../../contexts'
import { useModuleViewedTracking } from '../../hooks/tracking'

const CollectionDetails = ({ forwardedRef, information, title, position }) => {
  const { node_locale, currency } = useContext(LayoutContext)

  // IE doesn't support intersection observer, breaks the site
  const isIE =
    /*@cc_on!@*/ false ||
    (typeof window !== `undefined` && !!document.documentMode)
  const [ref, inView] = isIE ? [] : useInView({ triggerOnce: true })

  useEffect(() => {
    if (!isIE) {
      if (inView) {
        useModuleViewedTracking({
          currency: currency?.code,
          locale: node_locale,
          sectionType: 'Collection details',
          order: position,
        })
      }
    }
  }, [!isIE && inView])

  return (
    <div ref={forwardedRef}>
      <div ref={ref}>
        <StyledAccordion
          SEO
          items={information.map(({ title, content }) => ({
            title,
            content,
          }))}
          accordionTrackingData={{
            currency:
              node_locale === 'nl' ||
              node_locale === 'de' ||
              node_locale === 'fr' ||
              node_locale === 'en'
                ? 'EUR !'
                : node_locale === 'en-GB'
                ? 'GBP'
                : node_locale === 'en-US'
                ? 'USD'
                : 'EUR',
            locale: node_locale,
            viewType: 'Collection Page',
            sectionType: 'Collection details',
          }}
        />
      </div>
    </div>
  )
}

const StyledAccordion = styled(Accordion)`
  line-height: 1.4;
  margin-top: ${(props) => props.theme.space.marginMobile * 2}vw;
  margin-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;

  @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
    max-width: 49vw;
    margin-left: auto;
    margin-right: auto;
    margin-top: 0;
    margin-bottom: ${(props) => props.theme.space.marginDesktop * 6}vw;
  }

  li {
    border-bottom: 1px solid #e5e5e5;

    &:first-child {
      border-top: 1px solid #e5e5e5;

      @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
        border-top: none;
      }
    }

    &:last-child {
      @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
        border-bottom: none;
      }
    }
  }

  button {
    font-size: ${(props) => props.theme.fontSize.m30};
    position: relative;
    width: 100%;
    color: ${(props) => props.theme.colors.black};
    padding-right: ${(props) => props.theme.space.marginMobile * 2}vw;
    padding-left: ${(props) => props.theme.space.marginMobile}vw;
    padding-top: ${(props) => props.theme.space.marginMobile * 2}vw;
    padding-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d25};
      padding-right: ${(props) => props.theme.space.marginDesktop * 4}vw;
      padding-left: 0;
      padding-top: ${(props) => props.theme.space.marginDesktop * 4}vw;
      padding-bottom: ${(props) => props.theme.space.marginDesktop * 4}vw;
    }

    &::after {
      content: '';
      border: solid black;
      border-width: 0 0.4vw 0.4vw 0;
      display: block;
      width: 0.8vw;
      height: 0.8vw;
      transform: rotate(45deg);
      position: absolute;
      right: ${(props) => props.theme.space.marginMobile}vw;
      top: ${(props) => props.theme.space.marginMobile * 2.5}vw;

      @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
        border-width: 0 ${(props) => props.theme.space.marginDesktop / 5}vw
          ${(props) => props.theme.space.marginDesktop / 5}vw 0;
        width: ${(props) => props.theme.space.marginDesktop / 2}vw;
        height: ${(props) => props.theme.space.marginDesktop / 2}vw;
        right: ${(props) => props.theme.space.marginDesktop}vw;
        top: ${(props) => props.theme.space.marginDesktop * 5}vw;
      }
    }

    &.active::after {
      transform: rotate(-135deg);
    }
  }
  div {
    font-size: ${(props) => props.theme.fontSize.m26};
    padding-right: ${(props) => props.theme.space.marginMobile * 2}vw;
    padding-left: ${(props) => props.theme.space.marginMobile}vw;
    padding-bottom: ${(props) => props.theme.space.marginMobile * 2}vw;

    @media (min-width: ${(props) => props.theme.breakPoints.desktop}) {
      font-size: ${(props) => props.theme.fontSize.d14};
      padding-right: ${(props) => props.theme.space.marginDesktop * 8}vw;
      padding-left: 0;
      padding-bottom: ${(props) => props.theme.space.marginDesktop * 4}vw;
    }
  }
`

export default React.forwardRef((props, ref) => (
  <CollectionDetails {...props} forwardedRef={ref} />
))
